import React, { useState, useEffect, useCallback } from 'react';
import { callApi } from './api';

function GateAccessManager({ gate, ownerId, onFinished }) {
  const [eligibleUsers, setEligibleUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());

  const fetchEligibleUsers = useCallback(async () => {
    if (!ownerId) {
        setError("Kan ikke hente brukere: Eier-ID mangler.");
        setLoading(false);
        return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await callApi('listEligibleUsersForOwnerV2', { ownerId });
      
      const users = result.users;
      setEligibleUsers(users);

      // Initialize selected users based on who already has access
      const initialUserIds = new Set();
      for (const user of users) {
        if (user.customClaims?.gates?.includes(gate.id)) {
          initialUserIds.add(user.uid);
        }
      }
      setSelectedUserIds(initialUserIds);

    } catch (err) {
      setError('Kunne ikke hente brukere: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [gate.id, ownerId]);

  useEffect(() => {
    fetchEligibleUsers();
  }, [fetchEligibleUsers]);

  const handleSelectionChange = (userId) => {
    setSelectedUserIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    
    const initialUserIds = new Set(eligibleUsers.filter(u => u.customClaims?.gates?.includes(gate.id)).map(u => u.uid));

    const usersToAdd = [...selectedUserIds].filter(uid => !initialUserIds.has(uid));
    const usersToRemove = [...initialUserIds].filter(uid => !selectedUserIds.has(uid));

    const updatePromises = [];

    // Add gate access
    for (const uid of usersToAdd) {
        const user = eligibleUsers.find(u => u.uid === uid);
        const newGates = [...(user.customClaims?.gates || []), gate.id];
        updatePromises.push(callApi('updateUserV2', { uid, gates: newGates }));
    }

    // Remove gate access
    for (const uid of usersToRemove) {
        const user = eligibleUsers.find(u => u.uid === uid);
        const newGates = (user.customClaims?.gates || []).filter(gId => gId !== gate.id);
        updatePromises.push(callApi('updateUserV2', { uid, gates: newGates }));
    }

    try {
        await Promise.all(updatePromises);
        onFinished();
    } catch (err) {
        setError('Feil ved lagring av endringer: ' + err.message);
    } finally {
        setLoading(false);
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'var(--background-light)', color: 'var(--text-light)', padding: '20px', borderRadius: '5px', maxWidth: '90%', maxHeight: '90vh', overflowY: 'auto' }}>
        <h4>Administrer tilgang for port: {gate.name}</h4>
        
        {loading && <p>Laster...</p>}
        {error && <p style={{ color: 'red' }}>{error}</p>}

        {!loading && !error && (
          <div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {eligibleUsers.map(user => (
                <div key={user.uid}>
                  <label>
                    <input 
                      type="checkbox" 
                      checked={selectedUserIds.has(user.uid)}
                      onChange={() => handleSelectionChange(user.uid)}
                    />
                    {user.displayName || user.email} ({user.customClaims?.role?.name})
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
          <button type="button" onClick={onFinished}>Avbryt</button>
          <button type="button" onClick={handleSave} disabled={loading}>{loading ? 'Lagrer...' : 'Lagre Endringer'}</button>
        </div>
      </div>
    </div>
  );
}

export default GateAccessManager;
